<template>
  <div class="notes">
    <ez-image-modal ref="imageModal" />

    <div class="notes__list">
      <template v-if="notes.length">
      <note
        v-for="note in notes"
        :key="note.id"
        :isResponse="note.isResponse">
        <template #avatar>
          <ez-entity-info
            v-if="!!note.sender"
            imgWidth="2rem"
            imgHeight="2rem"
            :imgUrl="note.sender.logo || defaultImage"
            imgBorderRadius="50%">
          </ez-entity-info>
        </template>
        <template #actions>
          <font-awesome-icon
            v-if="note.canBeEdited"
            icon="pen"
            @click="openEdit(note)"
          />
          <font-awesome-icon
            v-if="note.canBeDeleted"
            icon="trash"
            @click="openDelete(note)"
          />
        </template>
        <template #date>{{ note.createdAt|date }}</template>
        <div>{{ note.message }}</div>
        <div v-if="note.images.length > 0" class="note__images">
          <div
            class="note__image"
            v-for="(image, id) in note.images"
            :key="id" @click="openImage(image)">
            <img :src="image" :alt="`note-${id}`">
          </div>
        </div>
      </note>
    </template>

    <empty-state v-else-if="!readOnly" class="notes__empty">
      <template #badge><img src="@/assets/no-notes-state.svg" alt="" /></template>
      <template #title>No notes here</template>
      <template #info>There are no notes included in this order.</template>
    </empty-state>
    </div>

    <ez-button
      v-if="canManageNotes && !readOnly"
      class="notes__add"
      type="secondary"
      formType="button"
      isFullWidth
      @click="openAdd"
    >
      <font-awesome-icon icon="pen" />
      Add Note
    </ez-button>

    <leave-note-modal v-if="!readOnly" ref="noteModal" @submit="onNoteSubmit" />

    <ez-confirmation-modal ref="deleteModal" type="red" icon="question">
      <template #title>Delete Note?</template>
      <template #content>
        <p>Are you sure you want to delete this note? This action cannot be undone.</p>
      </template>
      <template #footer>
        <ez-button type="link" formType="button" @click="closeDelete">Cancel</ez-button>
        <ez-button
          type="red"
          formType="button"
          :is-loading="isDeleting"
          @click="confirmDelete"
        >
          Delete
        </ez-button>
      </template>
    </ez-confirmation-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { EzImageModal, EzConfirmationModal } from '@/components/ui/Modal';
import EzEntityInfo from '@/components/ui/EntityInfo';
import EzButton from '@/components/ui/Button';
import EmptyState from '@/views/common/empty-state';
import flash from '@/components/ui/FlashMessage';
import { LOADING_KEY } from '@/util/constants';
import Note from './Note.vue';
import LeaveNoteModal from './LeaveNoteModal.vue';

export default {
  name: 'EzNotes',
  components: {
    EzImageModal,
    EzConfirmationModal,
    EzEntityInfo,
    EzButton,
    EmptyState,
    Note,
    LeaveNoteModal,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      noteToDelete: null,
    };
  },
  computed: {
    ...mapState('entities/users', ['loggedUser']),
    ...mapGetters('defaultImages', ['getDefaultImage']),
    ...mapGetters('loading', ['getLoading']),
    defaultImage() {
      return this.getDefaultImage('distributor');
    },
    canManageNotes() {
      const role = localStorage.getItem('role');
      return role === 'venue' || role === 'distributor';
    },
    isDeleting() {
      return this.getLoading(LOADING_KEY.DELETE_ORDER_NOTE);
    },
    notes() {
      const { order } = this;
      const isVenue = localStorage.getItem('role') === 'venue';
      const isDistributor = localStorage.getItem('role') === 'distributor';
      const isAdmin = localStorage.getItem('role') === 'admin';
      const venues = this.loggedUser?.venues || [];
      const venueSender = note => order.venue || venues.find(v => v.id === note.venueId) || null;
      return (order.notes || [])
        .map(note => ({
          ...note,
          sender: note.distributorId ? order.distributor : venueSender(note),
          isResponse: (isVenue && !!note.distributorId)
            || (isDistributor && !!note.venueId)
            || (isAdmin && !!note.distributorId),
        }));
    },
  },
  methods: {
    ...mapActions('entities/orders', ['createOrderNote', 'updateOrderNote', 'deleteOrderNote']),
    openImage(image) {
      this.$refs.imageModal.open(image);
    },
    openAdd() {
      this.$refs.noteModal.open();
    },
    openEdit(note) {
      this.$refs.noteModal.open(note);
    },
    async onNoteSubmit({ message, noteId }) {
      try {
        if (noteId) {
          await this.updateOrderNote({ orderId: this.order.id, noteId, message });
        } else {
          await this.createOrderNote({ orderId: this.order.id, message });
        }
        this.$refs.noteModal.close();
        flash.success({ title: `Note successfully ${noteId ? 'updated' : 'added'}.` });
        this.$emit('notesChanged');
      } catch (e) {
        flash.error({ title: 'Something went wrong!' });
      }
    },
    openDelete(note) {
      this.noteToDelete = note;
      this.$refs.deleteModal.open();
    },
    closeDelete() {
      this.$refs.deleteModal.close();
      this.noteToDelete = null;
    },
    async confirmDelete() {
      if (!this.noteToDelete) return;
      try {
        await this.deleteOrderNote({ orderId: this.order.id, noteId: this.noteToDelete.id });
        this.$refs.deleteModal.close();
        this.noteToDelete = null;
        flash.success({ title: 'Note successfully deleted.' });
        this.$emit('notesChanged');
      } catch (e) {
        flash.error({ title: 'Something went wrong!' });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.notes {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 100%;
}

.notes__list {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}

.notes__add {
  margin-top: 1rem;
}

.notes__empty {
  :deep() img {
    width: 200px;
    height: 88px;
  }
}

.note__images {
  @extend %flex-center;
  margin-top: px-to-rem(8px);
}

.note__image {
  @include size(40px);
  border: 1px solid #DEE1E4;
  border-radius: $border-radius;
  overflow: hidden;
  cursor: pointer;

  img {
    @include size(100%);
    object-fit: cover;
  }
  & + & { margin-left: px-to-rem(8px); }
}
</style>
